import { useEffect, useRef, useState } from "react";
import { FailureSeverity, ValidationResult } from "sprint-common-web";

import { useOrdersModuleApi } from "../../../../common/useOrdersModuleApi";
import { useCreateOrder } from "../provider/useCreateOrder";
import { getSequencedSoundtrackIds } from "../soundtrack/soundtrack";
import { getSequencedSubtitlesIds } from "../subtitles/subtitles";

export const useCloudAssetsValidation = () => {
    const {
        contentVersion,
        soundtracks,
        dynamicSubtitles,
        embeddedSubtitle,
        airline,
        system,
        encodeSpecification,
        targetResolution,
        originSubtitlesFileNames
    } = useCreateOrder();
    const { validateCloudAssets } = useOrdersModuleApi();
    const [cloudAssetsValidationResult, setCloudAssetsValidationResult] = useState<ValidationResult>();
    const [isCloudAssetsValidationInProgress, setIsCloudAssetsValidationInProgress] = useState<boolean>(false);
    const [hasCloudAssetsValidationError, setHasCloudAssetsValidationError] = useState<boolean>(false);
    const abortController = useRef<AbortController>(new AbortController());

    useEffect(() => {
        setCloudAssetsValidationResult(undefined);

        abortController.current.abort();
        abortController.current = new AbortController();

        if (contentVersion && soundtracks.length && airline && system && encodeSpecification && embeddedSubtitle) {
            setIsCloudAssetsValidationInProgress(true);
            validateCloudAssets(
                {
                    airlineId: airline.id,
                    content: { subtype: contentVersion.subtype, version: { id: contentVersion.version.id } },
                    systemId: system.id,
                    encodeSpecificationId: encodeSpecification.id,
                    embeddedSubtitleId: embeddedSubtitle.id,
                    sequencedSoundtrackIds: getSequencedSoundtrackIds({ soundtracks }),
                    sequencedSubtitlesIds: getSequencedSubtitlesIds({
                        dynamicSubtitles,
                        originSubtitlesFileNames
                    }),
                    targetResolutionId: targetResolution?.id
                },
                abortController.current.signal
            )
                .then((result) => {
                    setCloudAssetsValidationResult({
                        ...result,
                        failures: result.failures.map((failure, index) => ({
                            ...failure,
                            code: failure.code + `_${index + 1}`
                        }))
                    });

                    const hasErrors =
                        result?.failures.some((failure) => failure.severity === FailureSeverity.Error) ?? false;

                    setHasCloudAssetsValidationError(hasErrors);
                })
                .catch((error) => {
                    if (error?.name !== "AbortError") {
                        throw error;
                    }
                })
                .finally(() => {
                    setIsCloudAssetsValidationInProgress(false);
                });
        }
    }, [
        encodeSpecification,
        soundtracks,
        soundtracks.length,
        embeddedSubtitle,
        dynamicSubtitles,
        dynamicSubtitles.length,
        airline,
        targetResolution,
        system,
        contentVersion
    ]);

    return {
        cloudAssetsValidationResult,
        isCloudAssetsValidationInProgress,
        hasCloudAssetsValidationError
    };
};
